import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Building2,
  Check,
  ChevronDown,
  Clock,
  CreditCard,
  ExternalLink,
  History,
  RefreshCw,
  ShoppingCart,
  ShieldCheck,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  apiService,
  getApiErrorMessage,
  BillingAccount,
  BillingPricingConfig,
  BillingTransaction,
  CreditPackage,
  Plan,
} from '../services/api';
import { retrySafeRead } from '../services/safeRead';
import { COMPANY_CONTACT_URL } from '../brand';
import { resolvePlanCheckoutOutcome } from '../services/planCheckoutStatus';
import { commitOwnedSearch, hasCheckoutReturnParams, HistoryMode } from '../navigation/historyState';
import {
  buildCheckoutReturnUrl as buildTypedCheckoutReturnUrl,
  CHECKOUT_RETURN_KEYS,
  CheckoutFlow,
  isPlanCheckoutStatusReturn,
  parseCheckoutReturnParams,
  removeCheckoutReturnParams,
} from '../navigation/checkoutReturn';
import { BillingSection, getBillingSection } from '../navigation/billingSections';
import { formatCredits, formatPlanCredits, stripCreditUnits } from '../utils/credits';
import { useTabActivity } from '../contexts/TabActivityContext';
import './BillingTab.css';
import { getUpgradeLabel, STARTER_PLAN_PATH } from '../navigation/featureAccess';

const OWNED_KEYS = ['section'];
const BILLING_TRANSACTION_DISPLAY_LIMIT = 50;
const ENTERPRISE_CREDIT_PURCHASE_MESSAGE =
  'Enterprise credit purchases are managed by the VectorMethods team. Contact sales to update this account.';
const DISPLAY_BILLING_TRANSACTION_TYPES = [
  'credit_purchase',
  'refund',
  'adjustment',
  'bonus',
  'initial_credits',
] as const;

type CustomRules = {
  enabled: boolean;
  min_mt: number;
  max_mt: number;
  step_mt: number;
  eligible_plan_ids: string[];
};

const isEnterprisePlan = (plan?: Plan | null): boolean => {
  if (!plan) return false;
  return (
    String(plan.plan_id || '').trim().toLowerCase() === 'enterprise' ||
    plan.self_serve === false
  );
};

const isEnterpriseAccount = (
  account: BillingAccount | null,
  currentPlanId: string | null,
): boolean => (
  String(account?.plan_id || '').trim().toLowerCase() === 'enterprise' ||
  String(currentPlanId || '').trim().toLowerCase() === 'enterprise'
);

const normalizePlanLabel = (planId: string | null | undefined): string => {
  const normalized = String(planId || '').trim();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'selected plan';
};

const getTransactionSource = (transaction: BillingTransaction): string => (
  String(transaction.metadata?.source || '').trim().toLowerCase()
);

const formatTransactionTypeLabel = (transaction: BillingTransaction): string => {
  const source = getTransactionSource(transaction);
  if (source === 'subscription_cycle') return 'Subscription renewal';
  if (source === 'subscription_update') return 'Plan change invoice';
  if (transaction.metadata?.custom_purchase === true) return 'Custom credit purchase';
  if (transaction.package_id || transaction.metadata?.package_id) return 'Credit package purchase';
  return transaction.transaction_type.replace(/_/g, ' ');
};

const formatTransactionReference = (transaction: BillingTransaction): string => {
  const invoiceId = String(transaction.stripe_invoice_id || '').trim();
  if (invoiceId) return invoiceId;
  const paymentIntentId = String(transaction.stripe_payment_intent_id || '').trim();
  return paymentIntentId || '-';
};

const getTransactionInvoiceUrl = (transaction: BillingTransaction): string => {
  const invoiceUrl = String(transaction.stripe_invoice_url || '').trim();
  if (invoiceUrl) return invoiceUrl;
  return String(transaction.stripe_invoice_pdf_url || '').trim();
};

interface BillingTabProps {
  account: BillingAccount;
  refreshAccount: () => Promise<BillingAccount>;
}

const BillingTab: React.FC<BillingTabProps> = ({ account, refreshAccount }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isActive } = useTabActivity();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = getBillingSection(location.search);
  const isLegacySection = searchParams.get('section') === 'purchase';
  const intendedPackageId = String(searchParams.get('credit_package') || '').trim() || null;
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [transactions, setTransactions] = useState<BillingTransaction[]>([]);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const currentPlanId = account.plan_id;
  const isTrial = account.stripe_subscription_status === 'trialing';
  const trialEligible = account.trial_eligible === true;
  const [billingAction, setBillingAction] = useState<string | null>(null);
  const billingActionRef = React.useRef<string | null>(null);
  const [customRules, setCustomRules] = useState<CustomRules | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [customTouched, setCustomTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [packageLoadError, setPackageLoadError] = useState<string | null>(null);
  const [pricingLoadError, setPricingLoadError] = useState<string | null>(null);
  const [planLoadError, setPlanLoadError] = useState<string | null>(null);
  const [transactionLoadError, setTransactionLoadError] = useState<string | null>(null);
  const [checkoutStatusMessage, setCheckoutStatusMessage] = useState<string | null>(null);
  const [checkoutRefreshRevision, setCheckoutRefreshRevision] = useState(0);
  const [isCheckingCheckout, setIsCheckingCheckout] = useState(false);
  const checkoutCheckRef = React.useRef(false);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState<string | null>(null);
  const catalogLoadedPlanRef = React.useRef<string | null>(null);
  const catalogLoadRef = React.useRef<{ planId: string; promise: Promise<void> } | null>(null);
  const historyLoadedRef = React.useRef(false);
  const historyLoadRef = React.useRef<Promise<void> | null>(null);
  const refreshLoadRef = React.useRef<Promise<void> | null>(null);
  const changingPlanId = billingAction?.startsWith('plan:') ? billingAction.slice(5) : null;
  const actionBusy = Boolean(billingAction);

  const releaseBillingAction = useCallback(() => {
    billingActionRef.current = null;
    setBillingAction(null);
  }, []);

  useEffect(() => {
    if (!isActive || !pendingRedirectUrl) return;
    setPendingRedirectUrl(null);
    try {
      window.location.href = pendingRedirectUrl;
    } catch {
      setError('Unable to open checkout. Please try again.');
      releaseBillingAction();
    }
  }, [isActive, pendingRedirectUrl, releaseBillingAction]);

  const sortedPlans = useMemo(() => {
    return [...plans].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.plan_id.localeCompare(b.plan_id);
    });
  }, [plans]);

  const selfServePlans = useMemo(
    () => sortedPlans.filter((plan) => !isEnterprisePlan(plan)),
    [sortedPlans]
  );

  const plansById = useMemo(() => {
    const mapping = new Map<string, Plan>();
    sortedPlans.forEach((plan) => mapping.set(plan.plan_id, plan));
    return mapping;
  }, [sortedPlans]);

  const activePackages = useMemo(() => packages.filter((pkg) => pkg.is_active).sort(
    (a, b) => (a.mt_amount + a.bonus_mt) - (b.mt_amount + b.bonus_mt) || a.package_id.localeCompare(b.package_id),
  ), [packages]);

  const billingTransactions = transactions;

  const currentPlan = useMemo(
    () => sortedPlans.find((plan) => plan.plan_id === currentPlanId) || null,
    [currentPlanId, sortedPlans]
  );

  const enterpriseManagedAccount = useMemo(
    () => isEnterpriseAccount(account, currentPlanId),
    [account, currentPlanId]
  );

  const setSection = useCallback((section: BillingSection, mode: HistoryMode = 'push') => {
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: { section },
      mode,
      preserveKeys: [...CHECKOUT_RETURN_KEYS],
    });
  }, [location, navigate]);

  useEffect(() => {
    if (isActive && isLegacySection) setSection(activeTab, 'replace');
  }, [activeTab, isActive, isLegacySection, setSection]);

  const parseCustomRules = useCallback((pricing: BillingPricingConfig): CustomRules | null => {
    const customPurchase = pricing?.credit_purchase?.custom_purchase;
    if (!customPurchase || typeof customPurchase !== 'object') return null;
    const minMt = Number(customPurchase.min_mt);
    const maxMt = Number(customPurchase.max_mt);
    const stepMt = Number(customPurchase.step_mt);
    if (!Number.isSafeInteger(minMt) || !Number.isSafeInteger(maxMt) || !Number.isSafeInteger(stepMt)) return null;
    if (minMt <= 0 || maxMt < minMt || stepMt <= 0) return null;
    if (!Array.isArray(customPurchase.eligible_plan_ids)) return null;
    return {
      enabled: customPurchase.enabled === true && pricing.billing_controls?.allow_credit_purchases === true,
      min_mt: minMt,
      max_mt: maxMt,
      step_mt: stepMt,
      eligible_plan_ids: customPurchase.eligible_plan_ids.map((id: unknown) => String(id).trim().toLowerCase()),
    };
  }, []);

  const loadCatalog = useCallback((planId: string, force = false): Promise<void> => {
    if (catalogLoadRef.current?.planId === planId) return catalogLoadRef.current.promise;
    if (!force && catalogLoadedPlanRef.current === planId) return Promise.resolve();
    setIsCatalogLoading(true);
    setPackageLoadError(null);
    setPricingLoadError(null);
    setPlanLoadError(null);
    const request = Promise.allSettled([
      retrySafeRead(() => apiService.getCreditPackages()),
      retrySafeRead(() => apiService.getPlans()),
      retrySafeRead(() => apiService.getBillingPricingConfig()),
    ]).then(([packageResult, planResult, pricingResult]) => {
      if (catalogLoadRef.current?.promise !== request) return;
      if (packageResult.status === 'fulfilled') {
        setPackages(packageResult.value);
      } else {
        setPackageLoadError(getApiErrorMessage(packageResult.reason, 'Credit packages are temporarily unavailable.'));
      }
      if (planResult.status === 'fulfilled') {
        setPlans(planResult.value);
      } else {
        setPlanLoadError(getApiErrorMessage(planResult.reason, 'Plans are temporarily unavailable.'));
      }
      if (pricingResult.status === 'fulfilled') {
        const rules = parseCustomRules(pricingResult.value);
        setCustomRules(rules);
        setCustomAmount((previous) => rules ? (previous || String(Math.ceil(rules.min_mt / rules.step_mt) * rules.step_mt)) : '');
        if (!rules) setPricingLoadError('Custom credit options are temporarily unavailable. Refresh to retry.');
      } else {
        setPricingLoadError(getApiErrorMessage(pricingResult.reason, 'Custom credit pricing is temporarily unavailable.'));
      }
      catalogLoadedPlanRef.current = planId;
      catalogLoadRef.current = null;
      setIsCatalogLoading(false);
    });
    catalogLoadRef.current = { planId, promise: request };
    return request;
  }, [parseCustomRules]);

  const loadHistory = useCallback((force = false): Promise<void> => {
    if (historyLoadRef.current) return historyLoadRef.current;
    if (!force && historyLoadedRef.current) return Promise.resolve();
    setIsHistoryLoading(true);
    setTransactionLoadError(null);
    const request = (async () => {
      try {
        const page = await retrySafeRead(() => apiService.getBillingTransactions(
          BILLING_TRANSACTION_DISPLAY_LIMIT, 0, undefined, DISPLAY_BILLING_TRANSACTION_TYPES,
        ));
        setTransactions(page.transactions);
      } catch (err) {
        setTransactionLoadError(getApiErrorMessage(err, 'Billing transactions are temporarily unavailable. Refresh to retry.'));
      } finally {
        historyLoadedRef.current = true;
        historyLoadRef.current = null;
        setIsHistoryLoading(false);
      }
    })();
    historyLoadRef.current = request;
    return request;
  }, []);

  const loadSection = useCallback((section: BillingSection, planId: string, force = false) => (
    section === 'transactions' ? loadHistory(force) : loadCatalog(planId, force)
  ), [loadCatalog, loadHistory]);

  const loadData = useCallback((): Promise<void> => {
    if (refreshLoadRef.current) return refreshLoadRef.current;
    setIsRefreshing(true);
    setError(null);
    const request = (async () => {
      try {
        const refreshedAccount = await refreshAccount();
        await loadSection(activeTab, refreshedAccount.plan_id, true);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to refresh billing account. Please retry.'));
      } finally {
        refreshLoadRef.current = null;
        setIsRefreshing(false);
      }
    })();
    refreshLoadRef.current = request;
    return request;
  }, [activeTab, loadSection, refreshAccount]);

  useEffect(() => {
    // Checkout return handling owns its refresh; do not run a second mount load.
    if (isLegacySection || hasCheckoutReturnParams(location.search)) return;
    void loadSection(activeTab, account.plan_id);
  }, [account.plan_id, activeTab, isLegacySection, loadSection, location.search]);

  const clearCheckoutReturnParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const changed = removeCheckoutReturnParams(params);
    if (!changed) return;
    params.set('section', activeTab);
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true }
    );
  }, [activeTab, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isActive || isLegacySection) return;
    const checkoutReturn = parseCheckoutReturnParams(location.search);
    if (!checkoutReturn.isReturn) return;
    let cancelled = false;
    checkoutCheckRef.current = true;
    setIsCheckingCheckout(true);
    (async () => {
      let shouldClearReturnParams = false;
      try {
        if (!checkoutReturn.isValid) {
          setCheckoutStatusMessage('Checkout return was not recognized. Refresh billing after Stripe confirms payment.');
          shouldClearReturnParams = true;
          await loadData();
          return;
        }
        if (checkoutReturn.outcome === 'cancelled') {
          setCheckoutStatusMessage('Checkout cancelled.');
          shouldClearReturnParams = true;
          await loadData();
          return;
        }
        if (checkoutReturn.flow !== 'plan') {
          setCheckoutStatusMessage('Credit checkout complete. Credits will appear after Stripe confirms payment.');
          shouldClearReturnParams = true;
          await loadData();
          return;
        }
        if (!isPlanCheckoutStatusReturn(checkoutReturn)) {
          setCheckoutStatusMessage('Checkout return was not recognized. Refresh billing after Stripe confirms payment.');
          shouldClearReturnParams = true;
          await loadData();
          return;
        }
        setCheckoutStatusMessage('Checking payment status…');
        const status = await retrySafeRead(() => apiService.getPlanCheckoutStatus(checkoutReturn.sessionId));
        if (cancelled) return;
        if (status.last_payment_failure_message || status.pending_plan_status === 'payment_failed') {
          setCheckoutStatusMessage(status.last_payment_failure_message || 'Payment failed. Please retry.');
          shouldClearReturnParams = true;
        } else if (['awaiting_payment', 'payment_pending'].includes(status.pending_plan_status || '')) {
          setCheckoutStatusMessage('Payment is processing. Your plan will activate after Stripe confirms payment.');
        } else if (status.checkout_status === 'completed') {
          setCheckoutStatusMessage(status.stripe_subscription_status === 'trialing' ? 'Your Starter trial has started.' : 'Payment confirmed.');
          shouldClearReturnParams = true;
        } else if (['expired', 'failed', 'canceled', 'cancelled'].includes(status.checkout_status)) {
          setCheckoutStatusMessage('Checkout did not complete. Choose a plan to try again.');
          shouldClearReturnParams = true;
        } else {
          setCheckoutStatusMessage('Checkout is still pending. Refresh billing to check again.');
        }
        await loadData();
      } catch (err) {
        if (!cancelled) {
          if ((err as any)?.response?.status === 404) {
            setCheckoutStatusMessage('Checkout session was not recognized. Refresh billing after Stripe confirms payment.');
            shouldClearReturnParams = true;
          } else {
            setCheckoutStatusMessage(getApiErrorMessage(
              err, 'Checkout status is temporarily unavailable. Refresh billing to check your account.',
            ));
          }
          await loadData();
        }
      } finally {
        if (!cancelled) {
          checkoutCheckRef.current = false;
          setIsCheckingCheckout(false);
        }
        if (!cancelled && shouldClearReturnParams) {
          clearCheckoutReturnParams();
        }
      }
    })();
    return () => {
      cancelled = true;
      checkoutCheckRef.current = false;
      setIsCheckingCheckout(false);
    };
  }, [isActive, isLegacySection, checkoutRefreshRevision, clearCheckoutReturnParams, location.search, loadData]);

  const handleRefresh = () => {
    if (billingActionRef.current || checkoutCheckRef.current || refreshLoadRef.current) return;
    if (hasCheckoutReturnParams(location.search)) {
      checkoutCheckRef.current = true;
      setCheckoutRefreshRevision((revision) => revision + 1);
    } else {
      void loadData();
    }
  };

  useEffect(() => {
    // Back navigation can restore this exact page from the browser's page cache.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setPendingRedirectUrl(null);
      releaseBillingAction();
      void loadData();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [loadData, releaseBillingAction]);

  const firstCustomAmount = customRules
    ? Math.ceil(customRules.min_mt / customRules.step_mt) * customRules.step_mt
    : 0;
  const customEligible = Boolean(
    customRules?.enabled
    && !enterpriseManagedAccount
    && firstCustomAmount <= customRules.max_mt
    && (!customRules.eligible_plan_ids.length || customRules.eligible_plan_ids.includes(currentPlanId)),
  );
  const customValue = Number(customAmount);
  const customAmountError = !customRules ? null
    : !customAmount.trim() || !Number.isSafeInteger(customValue) || customValue <= 0
      ? 'Enter a positive whole number of credits.'
      : customValue < customRules.min_mt || customValue > customRules.max_mt
        ? `Custom amount must be between ${formatCredits(customRules.min_mt)} and ${formatCredits(customRules.max_mt)} credits.`
        : customValue % customRules.step_mt !== 0
          ? `Custom amount must be in increments of ${formatCredits(customRules.step_mt)} credits.`
          : null;

  const buildCheckoutReturnUrl = (flag: 'billing_success' | 'billing_cancelled', flow: CheckoutFlow) => {
    const returnUrl = new URL(window.location.href);
    returnUrl.pathname = '/billing';
    returnUrl.searchParams.set('section', flow === 'plan' ? 'plans' : 'credits');
    return buildTypedCheckoutReturnUrl(returnUrl.toString(), flag, flow);
  };

  const runBillingAction = async (
    action: string,
    operation: () => Promise<string | void>,
    failureMessage: string,
  ) => {
    // A ref closes the gap before React renders disabled controls. All billing
    // mutations share this lock, including a redirect waiting in a hidden tab.
    if (billingActionRef.current || isRefreshing || checkoutCheckRef.current || refreshLoadRef.current) return;
    billingActionRef.current = action;
    setBillingAction(action);
    setError(null);
    setCheckoutStatusMessage(null);
    let redirecting = false;
    try {
      const redirectUrl = await operation();
      if (redirectUrl !== undefined) {
        const parsedUrl = new URL(redirectUrl);
        if (!['https:', 'http:'].includes(parsedUrl.protocol)) throw new Error('Invalid checkout URL');
        redirecting = true;
        setPendingRedirectUrl(parsedUrl.toString());
      }
    } catch (err) {
      setError(getApiErrorMessage(err, failureMessage));
    } finally {
      if (!redirecting) releaseBillingAction();
    }
  };

  const handlePurchaseCredits = (packageId: string) => {
    const creditPackage = activePackages.find((pkg) => pkg.package_id === packageId);
    if (enterpriseManagedAccount) {
      setError(ENTERPRISE_CREDIT_PURCHASE_MESSAGE);
      return;
    }
    if (!creditPackage?.eligible || isCatalogLoading || packageLoadError) return;
    void runBillingAction(`package:${packageId}`, async () => {
      const response = await apiService.createCheckoutSession(
        packageId,
        buildCheckoutReturnUrl('billing_success', 'credits'),
        buildCheckoutReturnUrl('billing_cancelled', 'credits'),
      );
      if (!response.checkout_url) throw new Error('Checkout is temporarily unavailable. Please try again.');
      return response.checkout_url;
    }, 'Failed to initiate checkout. Please try again.');
  };

  const handlePurchaseCustomCredits = () => {
    setCustomTouched(true);
    if (!customEligible || customAmountError || isCatalogLoading || pricingLoadError) return;
    void runBillingAction('custom', async () => {
      const response = await apiService.createCustomCheckoutSession(
        customValue,
        buildCheckoutReturnUrl('billing_success', 'credits'),
        buildCheckoutReturnUrl('billing_cancelled', 'credits'),
      );
      if (!response.checkout_url) throw new Error('Checkout is temporarily unavailable. Please try again.');
      return response.checkout_url;
    }, 'Failed to initiate custom purchase. Please try again.');
  };

  const handleManageBilling = () => {
    void runBillingAction('portal', async () => {
      const response = await apiService.getBillingPortalUrl(window.location.href);
      if (!response.url) throw new Error('The billing portal is temporarily unavailable. Please try again.');
      return response.url;
    }, 'Failed to open billing portal. Please try again.');
  };

  const handleContactEnterprise = () => {
    window.open(COMPANY_CONTACT_URL, '_blank', 'noopener,noreferrer');
  };

  const handleChangePlan = (plan: Plan) => {
    if (currentPlanId === plan.plan_id || isCatalogLoading || planLoadError) return;
    if (enterpriseManagedAccount) {
      setError('Enterprise plan changes are managed by the VectorMethods team. Contact sales to update this account.');
      return;
    }
    if (isEnterprisePlan(plan)) {
      handleContactEnterprise();
      return;
    }
    void runBillingAction(`plan:${plan.plan_id}`, async () => {
      const subscriptionStatus = (account.stripe_subscription_status || '').toLowerCase();
      const hasManageableSubscription =
        Boolean(account.stripe_subscription_id)
        && ['active', 'trialing', 'past_due', 'unpaid'].includes(subscriptionStatus);

      if (plan.checkout_required && !hasManageableSubscription) {
        const checkout = await apiService.checkoutPlan(
          plan.plan_id,
          buildCheckoutReturnUrl('billing_success', 'plan'),
          buildCheckoutReturnUrl('billing_cancelled', 'plan'),
        );
        const outcome = resolvePlanCheckoutOutcome(checkout);
        if (outcome.kind === 'manual_required') {
          setError('This plan is managed by our team. Contact sales to activate it.');
          return;
        }
        if (outcome.kind === 'redirect') return outcome.checkoutUrl;
        if (outcome.kind === 'completed') {
          setCheckoutStatusMessage('Your subscription is ready.');
          await loadData();
          return;
        }
        if (outcome.kind === 'pending') {
          if (outcome.status === 'awaiting_payment' || outcome.status === 'payment_pending') {
            setCheckoutStatusMessage('Payment is processing. Your plan will activate after Stripe confirms payment.');
            await loadData();
            return;
          }
          setError('Checkout is still being prepared. Please retry in a moment.');
          return;
        }
        if (outcome.kind === 'terminal') {
          setError('The previous checkout is no longer active. Refresh this page and retry.');
          return;
        }
        if (outcome.kind === 'missing_url') throw new Error('Plan checkout did not return a redirect URL.');
      }
      await apiService.selectPlan(plan.plan_id);
      await loadData();
    }, 'Failed to change plan. Please try again.');
  };

  const formatDate = (dateString: string | null | undefined, fallback = 'N/A') => {
    if (!dateString) return fallback;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return fallback;
    return date.toLocaleString();
  };

  const formatCreditAmount = (amount: number) => formatCredits(amount, { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  const formatUSD = (amount: number | null) => {
    if (amount === null) return '-';
    return `$${amount.toFixed(2)}`;
  };

  const formatPackageEligibilityReason = (reason?: string | null) => {
    switch (reason) {
      case 'payment_processing_unconfigured':
        return 'Payment processing is not configured';
      case 'credit_purchases_disabled':
        return 'Credit purchases are disabled';
      case 'plan_not_eligible':
        return 'Unavailable for current plan';
      default:
        return 'Unavailable for current plan';
    }
  };

  const formatPendingPlanMessage = (billingAccount: BillingAccount): React.ReactNode => {
    const pendingPlanId = String(billingAccount.pending_plan_id || '').trim().toLowerCase();
    const pendingPlanName = plansById.get(pendingPlanId)?.name || normalizePlanLabel(pendingPlanId);
    const status = String(billingAccount.pending_plan_status || '').trim().toLowerCase();
    const changeType = String(billingAccount.pending_plan_change_type || '').trim().toLowerCase();
    const effectiveText = billingAccount.pending_plan_effective_at
      ? formatDate(billingAccount.pending_plan_effective_at)
      : 'the next billing cycle';

    if (status === 'payment_failed') {
      return (
        <>
          Payment failed for <strong>{pendingPlanName}</strong>. Retry checkout or choose a different plan.
        </>
      );
    }
    if (changeType === 'period_end' && pendingPlanId === 'free') {
      return (
        <>
          Cancellation scheduled: <strong>{currentPlan?.name || normalizePlanLabel(billingAccount.plan_id)}</strong> stays active until {effectiveText}, then switches to <strong>{pendingPlanName}</strong>.
        </>
      );
    }
    if (changeType === 'period_end') {
      return (
        <>
          Downgrade scheduled: <strong>{currentPlan?.name || normalizePlanLabel(billingAccount.plan_id)}</strong> stays active until {effectiveText}, then switches to <strong>{pendingPlanName}</strong>.
        </>
      );
    }
    if (status === 'awaiting_payment' || status === 'payment_pending' || changeType === 'payment_pending' || changeType === 'initial_checkout') {
      return (
        <>
          Payment pending: <strong>{pendingPlanName}</strong> activates after Stripe confirms payment.
        </>
      );
    }
    return (
      <>
        Pending change: <strong>{pendingPlanName}</strong>{status ? ` (${status.replace(/_/g, ' ')})` : ''} effective {effectiveText}.
      </>
    );
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'credit_purchase':
        return <TrendingUp size={16} style={{ color: 'var(--success-color)' }} />;
      case 'adjustment':
      case 'admin_adjustment':
        return <CreditCard size={16} style={{ color: 'var(--accent-color)' }} />;
      default:
        return <CreditCard size={16} />;
    }
  };

  const trialCancels = isTrial && account.pending_plan_id === 'free' && account.pending_plan_status === 'scheduled';
  const trialRenewalPlan = isTrial && account.pending_plan_status === 'scheduled'
    ? plansById.get(account.pending_plan_id || '') || currentPlan : currentPlan;
  const catalogBusy = isCatalogLoading || isRefreshing || isCheckingCheckout;
  const planDescriptions: Record<string, string> = {
    free: 'Upload and process media in Playground, and explore demo index search.',
    starter: 'More capacity for your day-to-day workflows.',
    pro: 'Higher throughput for growing production workloads.',
  };

  return (
    <div className="billing-tab">
      <div className="billing-container">
        <div className="billing-section">
          <header className="billing-page-header">
            <div>
              <p className="billing-eyebrow">Your workspace</p>
              <h1>Billing & Credits</h1>
              <p className="billing-page-description">Manage your plan, add credits, and review your billing activity.</p>
            </div>
            <div className="billing-page-controls">
              <button className="billing-icon-button" type="button" onClick={handleRefresh} disabled={isRefreshing || isCheckingCheckout || actionBusy} aria-label="Refresh billing" title="Refresh billing">
                <RefreshCw size={17} className={isRefreshing || isCheckingCheckout ? 'spinning' : ''} aria-hidden="true" />
              </button>
              {account.stripe_customer_id && (
                <button className="billing-button billing-button-secondary" type="button" onClick={handleManageBilling} disabled={actionBusy || isRefreshing || isCheckingCheckout}>
                  <ExternalLink size={15} aria-hidden="true" />
                  {billingAction === 'portal' ? 'Opening billing…' : 'Manage Billing'}
                </button>
              )}
            </div>
          </header>

          <section className="billing-overview" aria-label="Account overview">
            <div className="billing-overview-plan">
              <span className="billing-overview-label">Current plan</span>
              <div className="billing-overview-value">
                {currentPlan?.name || normalizePlanLabel(account.plan_id)}
                <span className="billing-plan-tag">{account.plan_id.toUpperCase()}</span>
              </div>
              <span className="billing-overview-caption">
                {isTrial ? 'All Starter features are available during your trial.' : account.plan_id === 'free' ? 'Use Playground for your media. Starter unlocks custom indexes and integrations.' : enterpriseManagedAccount ? 'Managed with the VectorMethods team.' : 'Your current workspace access.'}
              </span>
            </div>
            <div className="billing-overview-balance">
              <span className="billing-overview-label"><Wallet size={15} aria-hidden="true" />Current balance</span>
              <div className="billing-overview-value">{formatCreditAmount(account.balance_mt)} <small>credits</small></div>
              <span className="billing-overview-caption">
                {account.subscription_cycle_remaining_mt > 0
                  ? `${formatCreditAmount(account.subscription_cycle_remaining_mt)} subscription cycle credits remaining`
                  : 'Available for your next workflow.'}
              </span>
            </div>
            <div className="billing-overview-cycle">
              <span className="billing-overview-label">{isTrial ? 'Starter trial' : account.paid_through ? 'Current billing period' : 'Subscription'}</span>
              <div className="billing-overview-subvalue">{isTrial ? `Trial ends ${formatDate(account.trial_ends_at)}` : account.paid_through ? `Paid through ${formatDate(account.paid_through)}` : account.stripe_subscription_status ? normalizePlanLabel(account.stripe_subscription_status.replace(/_/g, ' ')) : 'No active subscription'}</div>
              <span className="billing-overview-caption">{trialCancels ? 'Cancellation is scheduled. You will not be charged when the trial ends.' : isTrial ? `Then ${trialRenewalPlan?.price_display || 'the selected monthly price'}.` : account.plan_id === 'free' ? 'Free includes Playground access with no subscription charge.' : 'Billing is managed securely through Stripe.'}</span>
            </div>
          </section>

          {error && (
            <div className="billing-inline-error" role="alert">
              <AlertCircle size={18} aria-hidden="true" />
              <span>{error}</span>
              <button className="billing-text-button" type="button" onClick={handleRefresh} disabled={isRefreshing || isCheckingCheckout || actionBusy}>Refresh</button>
            </div>
          )}
          {checkoutStatusMessage && (
            <div className="billing-inline-status" role="status">
              <Clock size={18} aria-hidden="true" /><span>{checkoutStatusMessage}</span>
            </div>
          )}
          {account.pending_plan_id && (
            <div className="billing-inline-status" role="status">
              <Clock size={18} aria-hidden="true" /><span>{formatPendingPlanMessage(account)}</span>
            </div>
          )}
          {account.last_payment_failure_message && (
            <div className="billing-inline-error" role="alert">
              <AlertCircle size={18} aria-hidden="true" /><span>{account.last_payment_failure_message}</span>
            </div>
          )}

          <nav className="section-tabs" aria-label="Billing sections">
            {([
              ['plans', 'Plans', CreditCard],
              ['credits', 'Add credits', Wallet],
              ['transactions', 'Transactions', History],
            ] as const).map(([section, label, Icon]) => (
              <button key={section} type="button" className={`section-tab ${activeTab === section ? 'active' : ''}`} onClick={() => setSection(section)} aria-current={activeTab === section ? 'page' : undefined}>
                <Icon size={16} aria-hidden="true" />{label}
              </button>
            ))}
          </nav>

          {activeTab !== 'transactions' && isCatalogLoading && (
            <div className="billing-loading" role="status"><RefreshCw size={16} className="spinning" aria-hidden="true" />Refreshing purchase options...</div>
          )}

          {activeTab === 'plans' && (
            <section className="billing-pane" aria-labelledby="billing-plans-title">
              <div className="billing-pane-heading">
                <div>
                  <h2 id="billing-plans-title">Choose the capacity you need</h2>
                  <p>Try Playground and demo search for free. Start a 7-day Starter trial with a card to unlock all Starter features, then continue on the monthly plan.</p>
                </div>
                <span className="billing-trust-note"><ShieldCheck size={16} aria-hidden="true" />Secure billing with Stripe</span>
              </div>
              {planLoadError && <div className="billing-inline-error" role="alert"><AlertCircle size={18} aria-hidden="true" /><span>{planLoadError}</span><button className="billing-text-button" onClick={handleRefresh} disabled={catalogBusy || actionBusy}>Retry</button></div>}
              {enterpriseManagedAccount && (
                <div className="billing-enterprise-panel">
                  <Building2 size={26} aria-hidden="true" />
                  <div><h3>Enterprise is admin-managed</h3><p>Enterprise upgrades, downgrades, and commercial changes are handled directly by the VectorMethods team.</p></div>
                  <button className="billing-button billing-button-secondary" type="button" onClick={handleContactEnterprise}>Contact sales<ExternalLink size={15} aria-hidden="true" /></button>
                </div>
              )}
              <>
                  <div className="billing-plan-grid" aria-busy={isCatalogLoading}>
                    {selfServePlans.map((plan) => {
                      const isCurrent = currentPlanId === plan.plan_id;
                      const isChanging = changingPlanId === plan.plan_id;
                      const isPending = account.pending_plan_id === plan.plan_id && ['scheduled', 'awaiting_payment', 'payment_pending'].includes(account.pending_plan_status || '');
                      const isDowngrade = plan.plan_id === 'free' || Boolean(currentPlan && plan.order < currentPlan.order);
                      const trialOffer = plan.plan_id === 'starter' && trialEligible && currentPlanId === 'free';
                      const label = enterpriseManagedAccount ? 'Managed by your team' : isCurrent ? 'Current plan' : isPending ? account.pending_plan_status === 'scheduled' || account.pending_plan_change_type === 'period_end' ? 'Change scheduled' : 'Payment pending' : trialOffer ? getUpgradeLabel(account) : currentPlanId === 'free' && plan.plan_id === 'starter' ? 'Subscribe to Starter' : isTrial && plan.plan_id === 'free' ? 'Cancel trial' : isTrial && plan.plan_id === 'pro' ? 'Switch to Pro after trial' : `${isDowngrade ? 'Downgrade' : 'Upgrade'} to ${plan.name}`;
                      return (
                        <article key={plan.plan_id} className={`billing-plan-card${plan.plan_id === 'starter' && currentPlanId === 'free' ? ' is-intended' : ''}${isCurrent ? ' is-current' : ''}${isPending ? ' is-pending' : ''}`}>
                          <div className="billing-plan-heading">
                            <h3>{plan.name}</h3>
                            {isCurrent ? <span className="billing-card-badge"><Check size={13} aria-hidden="true" />Current plan</span> : isPending ? <span className="billing-card-badge"><Clock size={13} aria-hidden="true" />Pending</span> : null}
                          </div>
                          <p className="billing-plan-description">{planDescriptions[plan.plan_id] || 'Credits and capacity for your workspace.'}</p>
                          <div className="billing-plan-price">{trialOffer ? `7 days free, then ${plan.price_display}` : plan.price_display}</div>
                          {trialOffer && <p>Card required.</p>}
                          <div className="billing-plan-credits">
                            <strong>{formatPlanCredits(plan.credits.display, plan.credits.mt)}</strong>
                            <span>{plan.plan_id === 'free' ? 'Playground and search credits for new accounts' : 'credits included per billing cycle'}</span>
                          </div>
                          <ul className="billing-plan-features">
                            {plan.features.map((feature) => <li key={feature}><Check size={16} aria-hidden="true" /><span>{feature}</span></li>)}
                            {plan.usage_discount_percent > 0 && <li><Check size={16} aria-hidden="true" /><span>{formatCredits(plan.usage_discount_percent)}% usage discount</span></li>}
                          </ul>
                          <div className="billing-plan-action">
                            <button type="button" className={`billing-button ${isCurrent || isDowngrade ? 'billing-button-secondary' : 'billing-button-primary'}`} disabled={enterpriseManagedAccount || isCurrent || isPending || actionBusy || catalogBusy || Boolean(planLoadError)} onClick={() => handleChangePlan(plan)}>
                              {isChanging ? <><RefreshCw size={16} className="spinning" aria-hidden="true" />Applying…</> : <>{label}{!enterpriseManagedAccount && !isCurrent && !isPending && <ArrowRight size={16} aria-hidden="true" />}</>}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  {!isCatalogLoading && !planLoadError && selfServePlans.length === 0 && <div className="billing-empty-state"><CreditCard size={28} aria-hidden="true" /><p>Plans are currently unavailable.</p><button type="button" className="billing-text-button" onClick={handleRefresh}>Refresh plans</button></div>}
                  {selfServePlans.length > 0 && (
                    <details className="billing-comparison">
                      <summary>Compare plan limits<ChevronDown size={17} aria-hidden="true" /></summary>
                      <div className="billing-table-scroll" tabIndex={0} aria-label="Plan limits comparison">
                        <table className="billing-comparison-table">
                          <thead><tr><th scope="col">Included capacity</th>{selfServePlans.map((plan) => <th scope="col" key={plan.plan_id}>{plan.name}{currentPlanId === plan.plan_id && <small>Current plan</small>}</th>)}</tr></thead>
                          <tbody>
                            {(['general', 'search', 'processing'] as const).map((bucket) => <tr key={bucket}><th scope="row">{bucket === 'general' ? 'General API requests' : bucket === 'search' ? 'Search requests' : 'Processing requests'}</th>{selfServePlans.map((plan) => <td key={plan.plan_id}><strong>{formatCredits(plan.limits[bucket].per_minute)} / min</strong><span>{formatCredits(plan.limits[bucket].per_hour)} / hour</span></td>)}</tr>)}
                            <tr><th scope="row">Usage discount</th>{selfServePlans.map((plan) => <td key={plan.plan_id}>{plan.usage_discount_percent > 0 ? `${formatCredits(plan.usage_discount_percent)}%` : 'Standard rates'}</td>)}</tr>
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                  {!enterpriseManagedAccount && sortedPlans.some(isEnterprisePlan) && <div className="billing-enterprise-panel"><Building2 size={26} aria-hidden="true" /><div><h3>Need a custom plan?</h3><p>Talk to us about Enterprise capacity, dedicated infrastructure, and rollout support.</p></div><button type="button" className="billing-button billing-button-secondary" onClick={handleContactEnterprise}>Contact sales<ExternalLink size={15} aria-hidden="true" /></button></div>}
                  <p className="billing-footnote">Your current plan stays active until a scheduled change takes effect. Payment and plan status are shown above.</p>
              </>
            </section>
          )}

          {activeTab === 'credits' && currentPlanId === 'free' && <section className="billing-pane" aria-label="Unlock credit purchases">
              <h2>Unlock your workspace</h2>
              <p>Subscribe or start a Starter trial before buying credits for your own media.</p>
              <button className="billing-button billing-button-primary" onClick={() => navigate(STARTER_PLAN_PATH)}>{getUpgradeLabel(account)}</button>
            </section>}
          {activeTab === 'credits' && currentPlanId !== 'free' && (
            <section className="billing-pane" aria-labelledby="billing-credits-title">
              <div className="billing-pane-heading">
                <div><h2 id="billing-credits-title">Add credits when you need them</h2><p>{enterpriseManagedAccount ? 'Your credit allocation is managed by the VectorMethods team.' : 'One-time purchases for more processing and search. Your plan stays the same.'}</p></div>
                {!enterpriseManagedAccount && <span className="billing-trust-note"><ShieldCheck size={16} aria-hidden="true" />Secure checkout with Stripe</span>}
              </div>
              {enterpriseManagedAccount && (
                <div className="billing-enterprise-panel"><Building2 size={26} aria-hidden="true" /><div><h3>Credit purchases are admin-managed</h3><p>Enterprise credit allocation, bulk usage packages, and payment terms are handled directly by the VectorMethods team.</p></div><button className="billing-button billing-button-secondary" type="button" onClick={handleContactEnterprise}>Contact sales<ExternalLink size={15} aria-hidden="true" /></button></div>
              )}
              <>
                  {packageLoadError && <div className="billing-inline-error" role="alert"><AlertCircle size={18} aria-hidden="true" /><span>{packageLoadError}</span><button className="billing-text-button" onClick={handleRefresh} disabled={catalogBusy || actionBusy}>Retry</button></div>}
                  <div className="billing-package-grid" aria-busy={isCatalogLoading}>
                    {activePackages.map((pkg) => {
                      const totalCredits = pkg.mt_amount + pkg.bonus_mt;
                      const isIntendedPackage = intendedPackageId === pkg.package_id;
                      const isPurchasing = billingAction === `package:${pkg.package_id}`;
                      return (
                        <article key={pkg.package_id} className={`billing-package-card${pkg.is_popular ? ' is-popular' : ''}${isIntendedPackage ? ' is-intended' : ''}`} data-intended-package={isIntendedPackage ? 'true' : undefined}>
                          <div className="billing-plan-heading"><h3>{pkg.name}</h3>{pkg.is_popular && <span className="billing-card-badge">Popular</span>}</div>
                          <div className="billing-package-credits"><strong>{formatCredits(totalCredits)}</strong><span>credits</span></div>
                          <p className="billing-package-bonus">{pkg.bonus_mt > 0 ? `${formatCredits(pkg.mt_amount)} + ${formatCredits(pkg.bonus_mt)} bonus credits` : 'A straightforward credit top-up'}</p>
                          <div className="billing-package-price"><strong>{formatUSD(pkg.price_usd)}</strong><span>USD · one-time payment</span></div>
                          <p className="billing-package-rate">{pkg.price_per_mt.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 })} per credit{pkg.bonus_mt > 0 ? ', including bonus' : ''}</p>
                          {pkg.description && <p className="billing-package-description">{stripCreditUnits(pkg.description.replace(/\bMT\b/g, 'credits'))}</p>}
                          {!enterpriseManagedAccount && !pkg.eligible && <p className="billing-package-unavailable">{formatPackageEligibilityReason(pkg.eligibility_reason)}</p>}
                          <div className="billing-package-action"><button className={`billing-button ${pkg.is_popular ? 'billing-button-primary' : 'billing-button-secondary'}`} type="button" onClick={() => handlePurchaseCredits(pkg.package_id)} disabled={enterpriseManagedAccount || !pkg.eligible || actionBusy || catalogBusy || Boolean(packageLoadError)}>
                            {enterpriseManagedAccount ? 'Managed by your team' : isPurchasing ? <><RefreshCw size={16} className="spinning" aria-hidden="true" />Opening checkout…</> : <>Buy {formatCredits(totalCredits)} credits<ArrowRight size={16} aria-hidden="true" /></>}
                          </button></div>
                        </article>
                      );
                    })}
                  </div>
                  {!enterpriseManagedAccount && !isCatalogLoading && !packageLoadError && activePackages.length === 0 && <div className="billing-empty-state"><ShoppingCart size={28} aria-hidden="true" /><p>No credit packages are currently available</p></div>}
                  <section className="billing-custom-card" aria-labelledby="billing-custom-title">
                    <div className="billing-custom-copy"><span className="billing-eyebrow">Choose your amount</span><h3 id="billing-custom-title">Custom credits</h3><p>{enterpriseManagedAccount ? 'Discuss custom credit allocation and payment terms with our team.' : customRules ? `Choose ${formatCredits(customRules.min_mt)}–${formatCredits(customRules.max_mt)} credits in increments of ${formatCredits(customRules.step_mt)}.` : 'Purchase the credits your next workflow needs.'}</p>{!enterpriseManagedAccount && <p className="billing-custom-price-note"><ShieldCheck size={15} aria-hidden="true" />Review the final price in Stripe before payment.</p>}</div>
                    {enterpriseManagedAccount ? <p className="billing-custom-unavailable">Custom credit purchases are managed with your Enterprise plan.</p> : pricingLoadError ? <div className="billing-inline-error" role="alert"><AlertCircle size={18} aria-hidden="true" /><span>{pricingLoadError}</span><button className="billing-text-button" onClick={handleRefresh} disabled={catalogBusy || actionBusy}>Retry</button></div> : customRules && !customEligible ? <p className="billing-custom-unavailable">{customRules.enabled ? 'Custom credit purchases are not available for your current plan.' : 'Custom credit purchases are currently unavailable.'}</p> : customEligible && (
                      <form className="billing-custom-form" noValidate onSubmit={(event) => { event.preventDefault(); handlePurchaseCustomCredits(); }}>
                        <label htmlFor="billing-custom-amount">Number of credits</label>
                        <div className="billing-custom-controls">
                          <input id="billing-custom-amount" type="number" inputMode="numeric" value={customAmount} onChange={(event) => { setCustomAmount(event.target.value); setCustomTouched(true); }} onBlur={() => setCustomTouched(true)} min={firstCustomAmount} max={customRules!.max_mt} step={customRules!.step_mt} disabled={actionBusy || catalogBusy} aria-invalid={customTouched && Boolean(customAmountError)} aria-describedby={customTouched && customAmountError ? 'billing-custom-error' : 'billing-custom-help'} />
                          <button type="submit" className="billing-button billing-button-primary" disabled={actionBusy || catalogBusy || Boolean(customAmountError)}>{billingAction === 'custom' ? 'Opening checkout…' : 'Continue to checkout'}<ArrowRight size={16} aria-hidden="true" /></button>
                        </div>
                        {customTouched && customAmountError ? <p id="billing-custom-error" className="billing-field-error" role="alert">{customAmountError}</p> : <p id="billing-custom-help" className="billing-field-help">A one-time purchase. Review your total before you pay.</p>}
                      </form>
                    )}
                  </section>
                  <p className="billing-footnote">{enterpriseManagedAccount ? 'Contact your team to discuss credit allocation.' : 'Credits are added after Stripe confirms payment. Buying credits does not change your plan or its limits.'}</p>
              </>
            </section>
          )}

          {activeTab === 'transactions' && (
            <section className="billing-pane" aria-labelledby="billing-history-title">
              <div className="billing-pane-heading"><div><h2 id="billing-history-title">Transactions</h2><p>Payments, credit purchases, plan changes, and account adjustments.</p></div></div>
              <div className="billing-details-grid">
                <div className="billing-detail-card"><span>Account created</span><strong>{formatDate(account.created_at)}</strong></div>
                <div className="billing-detail-card"><span>Paid through</span><strong>{formatDate(account.paid_through, 'Not active')}</strong></div>
                <div className="billing-detail-card"><span>Pending plan</span><strong>{account.pending_plan_id ? normalizePlanLabel(account.pending_plan_id) : 'None'}</strong></div>
                <div className="billing-detail-card"><span>Auto recharge</span><strong>{account.auto_recharge_enabled ? 'Enabled' : 'Disabled'}</strong></div>
              </div>
              {transactionLoadError && <div className="billing-inline-error" role="alert"><AlertCircle size={18} aria-hidden="true" /><span>{transactionLoadError}</span><button className="billing-text-button" onClick={handleRefresh} disabled={isRefreshing || isCheckingCheckout || actionBusy}>Retry</button></div>}
              {isHistoryLoading && <div className="billing-loading" role="status"><RefreshCw size={16} className="spinning" aria-hidden="true" /><span>Refreshing transactions...</span></div>}
              {billingTransactions.length === 0 ? !isHistoryLoading && !transactionLoadError && <div className="billing-empty-state"><History size={28} aria-hidden="true" /><p>No billing transactions yet</p><span>Your payments and credit purchases will appear here.</span></div> : (
                <div className="billing-table-scroll" tabIndex={0} aria-label="Billing transactions">
                  <table className="transactions-table">
                    <thead><tr><th scope="col">Date</th><th scope="col">Type</th><th scope="col">Description</th><th scope="col">Reference</th><th scope="col">Credits</th><th scope="col">USD</th></tr></thead>
                    <tbody>{billingTransactions.map((tx) => {
                      const invoiceUrl = getTransactionInvoiceUrl(tx);
                      const reference = formatTransactionReference(tx);
                      return <tr key={tx.transaction_id}><td className="timestamp-cell">{formatDate(tx.created_at)}</td><td className="type-cell"><span>{getTransactionTypeIcon(tx.transaction_type)}{formatTransactionTypeLabel(tx)}</span></td><td className="description-cell">{tx.description}</td><td className="reference-cell">{invoiceUrl ? <a href={invoiceUrl} target="_blank" rel="noreferrer">{reference}<ExternalLink size={13} aria-hidden="true" /></a> : reference}</td><td className={`amount-cell ${tx.amount_mt >= 0 ? 'positive' : 'negative'}`}>{tx.amount_mt >= 0 ? '+' : ''}{formatCreditAmount(tx.amount_mt)}</td><td className="usd-cell">{formatUSD(tx.amount_usd)}</td></tr>;
                    })}</tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingTab;
